/**
 * Public image proxy — resizes + converts to WebP via wsrv.nl (weserv.nl).
 * Massively reduces payload for hotlinked PNGs (60-80% smaller).
 *
 * Usage: <img src={cdnImg(URL, 800)} width={800} height={600} />
 *
 * Falls back to the original URL if the proxy fails to load.
 */
const WSRV = "https://wsrv.nl/";

/**
 * Return a resized/webp-optimised URL served through wsrv.nl.
 * @param {string} src   Original absolute image URL.
 * @param {number} [w]   Target max width in CSS px (defaults to none — proxy only).
 * @param {object} [opts]
 * @param {number} [opts.q=80]        WebP quality (1-100).
 * @param {string} [opts.output=webp] Output format.
 * @param {number} [opts.dpr]         Device pixel ratio multiplier.
 */
export function cdnImg(src, w, opts = {}) {
  if (!src || typeof src !== "string") return src;
  // Skip already-proxied, blob:, data:, and same-origin URLs
  if (src.startsWith(WSRV) || src.startsWith("data:") || src.startsWith("blob:")) return src;
  try {
    const noProto = src.replace(/^https?:\/\//, "");
    const params = new URLSearchParams();
    params.set("url", noProto);
    if (w) params.set("w", String(Math.round(w * (opts.dpr || 1))));
    params.set("output", opts.output || "webp");
    params.set("q", String(opts.q ?? 80));
    // Prefer no-upscale + smart fit
    params.set("we", "");
    return `${WSRV}?${params.toString()}`;
  } catch {
    return src;
  }
}

/**
 * Generate a responsive srcset for a source URL at multiple widths.
 * Widths default to a sensible set for hero images.
 */
export function cdnSrcSet(src, widths = [400, 640, 960, 1280, 1600]) {
  return widths.map((w) => `${cdnImg(src, w)} ${w}w`).join(", ");
}
